// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./lang/i18n";
// Roboto: loaded from public/fonts/roboto/roboto.css (fully offline). Run npm run download-fonts once if missing.
// Noto Sans Arabic for Dari & Pashto (self-hosted via @fontsource)
import "@fontsource/noto-sans-arabic/400.css";
import "@fontsource/noto-sans-arabic/500.css";
import "@fontsource/noto-sans-arabic/600.css";
import "@fontsource/noto-sans-arabic/700.css";
import "./api/axiosSetup"; // Centralized API error handling - must load before any API calls
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { AuthProvider, useAuth } from "./context";
import { getAccess } from "./permissions/Permissions";
import { createAppRouter } from "./Routes/AppRoutes";
import { NewRecordsProvider } from "./context/NewRecordsProvider";
import AppThemeProvider from "./Components/theme/AppThemeProvider";
import useFetchObjects from "./api/useFetchObjects";

const queryClient = new QueryClient({
  defaultOptions: {},
});

const AppWithRouter = () => {
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user;
  const userId = user?.user?.id;

  const { data } = useFetchObjects(
    ["permissions-users", userId],
    userId ? `users/${userId}/` : "",
    token,
    { enabled: !!userId && !!token }
  );

  const transformPermissions = (data) => {
    if (!data) return {};

    return {
      reception: data.reception || false,
      pharmacy: data.pharmacy || false,
      doctor: data.doctor || false,
      nurse: data.nurse || false,
      x_ray: data.x_ray || false,
      ultrasounds: data.ultrasounds || false,
      laboratory: data.laboratory || false,
      echo: data.echo || false,
      finance: data.finance || false,
      hr: data.hr || false,
      academic: data.academic || false,
      dental: data.dental || false,
      call_center: data.call_center || false,
      limited_data_view: data.limited_data_view || false,
      is_manager_in_call_center: data.is_manager_in_call_center || null,
      beauty: data.beauty || false,
      htPatient: data.htPatient || false,
      operations: data.operations || false,
      emergency: data.emergency || false,
      bedManagement: data.bedManagement || false,
      ipd: data.ipd || false,
      icu: data.icu || false,
      reports: data.reports || false,
      night_shift: data.night_shift || false,
      night_shift_manage: data.night_shift_manage || false,
    };
  };

  const permissions = transformPermissions(data);
  console.log(permissions);

  const userPermissions = getAccess(permissions, user?.user?.isAdmin);
  const router = createAppRouter(userPermissions);

  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <AppThemeProvider>
    <AuthProvider>
      <NewRecordsProvider>
        <ToastContainer />
        <QueryClientProvider client={queryClient}>
          <SnackbarProvider>
            <React.StrictMode>
              <AppWithRouter />
            </React.StrictMode>
          </SnackbarProvider>
        </QueryClientProvider>
      </NewRecordsProvider>
    </AuthProvider>
  </AppThemeProvider>
);
